import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import dashboardService from '../services/dashboardService';
import { leaveService } from '../services/leaveService';
import { payrollService } from '../services/payrollService';
import { employeeService } from '../services/api';
import { Link } from 'react-router-dom';
import LeaveRequestForm from './LeaveRequestForm';
import PayrollForm from './PayrollForm';
import PayrollBatchForm from './PayrollBatchForm';
import './Dashboard.css';

const Dashboard = () => {
  const { hasRole, hasModulePermission } = useAuth();
  const canExportDashboard = hasModulePermission('DASHBOARD', 'view');
  const canCreateLeave = hasModulePermission('LEAVES', 'create');
  const canCreatePayroll = hasModulePermission('PAYROLLS', 'create');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Quick Actions states
  const [showLeaveQuickAction, setShowLeaveQuickAction] = useState(false);
  const [showPayrollQuickAction, setShowPayrollQuickAction] = useState(false);
  const [selectedEmployeeForLeave, setSelectedEmployeeForLeave] = useState(null);
  const [selectedEmployeeForPayroll, setSelectedEmployeeForPayroll] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearchQueryPayroll, setEmployeeSearchQueryPayroll] = useState('');
  const [showEmployeeDropdownPayroll, setShowEmployeeDropdownPayroll] = useState(false);

  useEffect(() => {
    loadStats();
    loadEmployees();
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(loadStats, 300000);
    return () => clearInterval(interval);
  }, []);
  
  const loadEmployees = async () => {
    try {
      const response = await employeeService.getAll();
      setEmployees(response.data || []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getStats();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!canExportDashboard) {
      alert('Vous n\'avez pas les permissions nécessaires pour exporter le dashboard');
      return;
    }

    try {
      setExporting(true);
      const blob = await dashboardService.exportPdf();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_rh_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!canExportDashboard) {
      alert('Vous n\'avez pas les permissions nécessaires pour exporter le dashboard');
      return;
    }

    try {
      setExporting(true);
      const blob = await dashboardService.exportExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_rh_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      alert('Erreur lors de l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Quick Actions handlers
  const handleOpenLeaveQuickAction = () => {
    setSelectedEmployeeForLeave(null);
    setEmployeeSearchQuery('');
    setShowLeaveQuickAction(true);
  };

  const handleCloseLeaveQuickAction = () => {
    setShowLeaveQuickAction(false);
    setSelectedEmployeeForLeave(null);
    setEmployeeSearchQuery('');
  };

  const handleOpenPayrollQuickAction = () => {
    setSelectedEmployeeForPayroll(null);
    setEmployeeSearchQueryPayroll('');
    setShowPayrollQuickAction(true);
  };

  const handleClosePayrollQuickAction = () => {
    setShowPayrollQuickAction(false);
    setSelectedEmployeeForPayroll(null);
    setEmployeeSearchQueryPayroll('');
  };

  const handleLeaveSuccess = () => {
    handleCloseLeaveQuickAction();
    loadStats(); // Rafraîchir les stats
  };

  const handlePayrollSuccess = () => {
    handleClosePayrollQuickAction();
    loadStats(); // Rafraîchir les stats
  };

  // Employee autocomplete for leave
  const filteredEmployeesForLeave = employees.filter((emp) => {
    if (!employeeSearchQuery) return true;
    const q = employeeSearchQuery.toLowerCase();
    const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
    const email = (emp.email || '').toLowerCase();
    const matricule = (emp.matricule || '').toLowerCase();
    const cin = (emp.cin || '').toLowerCase();
    const cnss = (emp.cnssNumber || '').toLowerCase();
    return fullName.includes(q) || email.includes(q) || matricule.includes(q) || cin.includes(q) || cnss.includes(q);
  });

  // Employee autocomplete for payroll
  const filteredEmployeesForPayroll = employees.filter((emp) => {
    if (!employeeSearchQueryPayroll) return true;
    const q = employeeSearchQueryPayroll.toLowerCase();
    const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
    const email = (emp.email || '').toLowerCase();
    const matricule = (emp.matricule || '').toLowerCase();
    const cin = (emp.cin || '').toLowerCase();
    const cnss = (emp.cnssNumber || '').toLowerCase();
    return fullName.includes(q) || email.includes(q) || matricule.includes(q) || cin.includes(q) || cnss.includes(q);
  });

  const handleEmployeeSelectForLeave = (employee) => {
    if (employee) {
      setSelectedEmployeeForLeave(employee);
      setEmployeeSearchQuery(`${employee.nom} ${employee.prenom}`);
    } else {
      setSelectedEmployeeForLeave(null);
      setEmployeeSearchQuery('');
    }
    setShowEmployeeDropdown(false);
  };

  const handleEmployeeSelectForPayroll = (employee) => {
    if (employee) {
      setSelectedEmployeeForPayroll(employee);
      setEmployeeSearchQueryPayroll(`${employee.nom} ${employee.prenom}`);
    } else {
      setSelectedEmployeeForPayroll(null);
      setEmployeeSearchQueryPayroll('');
    }
    setShowEmployeeDropdownPayroll(false);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Chargement du dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error">{error}</div>
        <button onClick={loadStats} className="btn btn-primary">
          Réessayer
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Tableau de Bord RH</h1>
        {canExportDashboard && (
          <div className="export-buttons" style={{ gap: 8, display: 'flex', flexWrap: 'wrap' }}>
            <button
              onClick={handleExportPdf}
              disabled={exporting}
              className="btn btn-export"
            >
              {exporting ? 'Export...' : '📄 Exporter PDF'}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="btn btn-export"
            >
              {exporting ? 'Export...' : '📊 Exporter Excel'}
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions Section */}
      {(canCreateLeave || canCreatePayroll) && (
        <div className="quick-actions-section">
          <h2>Actions Rapides</h2>
          <div className="quick-actions-grid">
            {canCreateLeave && (
              <div className="quick-action-card">
                <div className="quick-action-icon">🏖️</div>
                <h3>Créer un Congé</h3>
                <p>Créer rapidement une demande de congé pour un employé</p>
                <button 
                  onClick={handleOpenLeaveQuickAction}
                  className="btn btn-primary"
                >
                  Nouveau Congé
                </button>
              </div>
            )}
            {canCreatePayroll && (
              <div className="quick-action-card">
                <div className="quick-action-icon">💰</div>
                <h3>Créer une Fiche de Paie</h3>
                <p>Créer rapidement une fiche de paie pour un employé</p>
                <button 
                  onClick={handleOpenPayrollQuickAction}
                  className="btn btn-primary"
                >
                  Nouvelle Paie
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Action Modal: Leave */}
      {showLeaveQuickAction && (
        <div className="modal-overlay" onClick={handleCloseLeaveQuickAction}>
          <div className="modal-content quick-action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Créer un Congé - Action Rapide</h2>
              <button className="close-button" onClick={handleCloseLeaveQuickAction}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="employee-search-leave">Sélectionner un employé *</label>
                <div className="employee-select-wrapper">
                  <input
                    type="text"
                    id="employee-search-leave"
                    className="employee-search-input"
                    placeholder="Rechercher un employé (nom, prénom, email, CIN)..."
                    value={employeeSearchQuery}
                    onChange={(e) => {
                      setEmployeeSearchQuery(e.target.value);
                      setShowEmployeeDropdown(true);
                    }}
                    onFocus={() => setShowEmployeeDropdown(true)}
                    onBlur={() => setTimeout(() => setShowEmployeeDropdown(false), 200)}
                  />
                  {showEmployeeDropdown && filteredEmployeesForLeave.length > 0 && (
                    <div 
                      className="employee-dropdown"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {filteredEmployeesForLeave.slice(0, 10).map((emp) => (
                        <div
                          key={emp.id}
                          className="employee-dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEmployeeSelectForLeave(emp);
                          }}
                        >
                          <div className="employee-name">{emp.nom} {emp.prenom}</div>
                          <div className="employee-details">{emp.email} | CIN: {emp.cin}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedEmployeeForLeave && (
                  <div className="selected-employee-badge">
                    ✓ Employé sélectionné: {selectedEmployeeForLeave.nom} {selectedEmployeeForLeave.prenom}
                  </div>
                )}
              </div>
              
              {selectedEmployeeForLeave && (
                <LeaveRequestForm
                  employeeId={selectedEmployeeForLeave.id}
                  employeeName={`${selectedEmployeeForLeave.nom} ${selectedEmployeeForLeave.prenom}`}
                  onSuccess={handleLeaveSuccess}
                  onClose={handleCloseLeaveQuickAction}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Action Modal: Payroll */}
      {showPayrollQuickAction && (
        <div className="modal-overlay" onClick={handleClosePayrollQuickAction}>
          <div className="modal-content quick-action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Créer une Fiche de Paie - Action Rapide</h2>
              <button className="close-button" onClick={handleClosePayrollQuickAction}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="employee-search-payroll">Sélectionner un employé *</label>
                <div className="employee-select-wrapper">
                  <input
                    type="text"
                    id="employee-search-payroll"
                    className="employee-search-input"
                    placeholder="Rechercher un employé (nom, prénom, email, CIN)..."
                    value={employeeSearchQueryPayroll}
                    onChange={(e) => {
                      setEmployeeSearchQueryPayroll(e.target.value);
                      setShowEmployeeDropdownPayroll(true);
                    }}
                    onFocus={() => setShowEmployeeDropdownPayroll(true)}
                    onBlur={() => setTimeout(() => setShowEmployeeDropdownPayroll(false), 200)}
                  />
                  {showEmployeeDropdownPayroll && filteredEmployeesForPayroll.length > 0 && (
                    <div 
                      className="employee-dropdown"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {filteredEmployeesForPayroll.slice(0, 10).map((emp) => (
                        <div
                          key={emp.id}
                          className="employee-dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEmployeeSelectForPayroll(emp);
                          }}
                        >
                          <div className="employee-name">{emp.nom} {emp.prenom}</div>
                          <div className="employee-details">{emp.email} | CIN: {emp.cin}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedEmployeeForPayroll && (
                  <div className="selected-employee-badge">
                    ✓ Employé sélectionné: {selectedEmployeeForPayroll.nom} {selectedEmployeeForPayroll.prenom}
                  </div>
                )}
              </div>
              
              {selectedEmployeeForPayroll && (
                <PayrollBatchForm
                  employeeId={selectedEmployeeForPayroll.id}
                  employeeName={`${selectedEmployeeForPayroll.nom} ${selectedEmployeeForPayroll.prenom}`}
                  employees={employees}
                  onSave={handlePayrollSuccess}
                  onCancel={handleClosePayrollQuickAction}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="stats-grid">
        {/* Statistiques Employés */}
        <div className="stat-card">
          <h3>Employés</h3>
          <div className="stat-value">{stats.totalEmployees}</div>
          <div className="stat-details">
            <span className="stat-item">
              <span className="stat-label">Actifs:</span>
              <span className="stat-number">{stats.activeEmployees}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Inactifs:</span>
              <span className="stat-number">{stats.inactiveEmployees}</span>
            </span>
          </div>
        </div>

        {/* Statistiques Congés */}
        <div className="stat-card">
          <h3>Congés</h3>
          <div className="stat-value highlight">{stats.pendingLeaves}</div>
          <div className="stat-label-small">En attente</div>
          <div className="stat-details">
            <span className="stat-item">
              <span className="stat-label">Approuvés:</span>
              <span className="stat-number">{stats.approvedLeaves}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Rejetés:</span>
              <span className="stat-number">{stats.rejectedLeaves}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Ce mois:</span>
              <span className="stat-number">{stats.totalLeavesThisMonth}</span>
            </span>
          </div>
        </div>

        {/* Taux d'Absentéisme */}
        <div className="stat-card">
          <h3>Taux d'Absentéisme</h3>
          <div className="stat-value large">{stats.absenteeismRate}%</div>
          <div className="stat-label-small">Ce mois</div>
        </div>

        {/* Statistiques Paie */}
        <div className="stat-card">
          <h3>Paie</h3>
          <div className="stat-value">{stats.totalPayrolls}</div>
          <div className="stat-details">
            <span className="stat-item">
              <span className="stat-label">En attente:</span>
              <span className="stat-number">{stats.pendingPayrolls}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Payées:</span>
              <span className="stat-number">{stats.paidPayrolls}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="charts-section">
        <div className="chart-card">
          <h3>Absences par Mois (12 derniers mois)</h3>
          <AbsencesByMonthChart data={stats.absencesByMonth || []} />
        </div>

        <div className="chart-card">
          <h3>Absences par Service</h3>
          <AbsencesByServiceChart data={stats.absencesByService || []} />
        </div>
      </div>
    </div>
  );
};

// Composant graphique simple pour les absences par mois
const AbsencesByMonthChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="no-data">Aucune donnée disponible</div>;
  }

  const maxDays = Math.max(...data.map(d => d.totalDays), 1);

  return (
    <div className="chart-container">
      <div className="bar-chart">
        {data.map((month, index) => {
          const height = (month.totalDays / maxDays) * 100;
          return (
            <div key={index} className="bar-item">
              <div className="bar-wrapper">
                <div
                  className="bar"
                  style={{ height: `${height}%` }}
                  title={`${month.monthLabel}: ${month.totalDays} jours`}
                >
                  <span className="bar-value">{month.totalDays}</span>
                </div>
              </div>
              <div className="bar-label">
                {month.monthLabel.split(' ')[0].substring(0, 3)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Composant graphique simple pour les absences par service
const AbsencesByServiceChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="no-data">Aucune donnée disponible</div>;
  }

  const maxAbsences = Math.max(...data.map(d => d.totalAbsences), 1);
  const topServices = data.slice(0, 10); // Top 10 services

  return (
    <div className="chart-container">
      <div className="bar-chart horizontal">
        {topServices.map((service, index) => {
          const width = (service.totalAbsences / maxAbsences) * 100;
          return (
            <div key={index} className="bar-item-horizontal">
              <div className="bar-label-horizontal">{service.service}</div>
              <div className="bar-wrapper-horizontal">
                <div
                  className="bar-horizontal"
                  style={{ width: `${width}%` }}
                  title={`${service.service}: ${service.totalAbsences} absences, ${service.totalDays} jours`}
                >
                  <span className="bar-value-horizontal">
                    {service.totalAbsences} ({service.totalDays}j)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;

